// Shared response-body parsing for the API client (api.js). Dependency-free so
// it can be unit-tested under bun (api.js itself pulls in auth.js and the
// vendored Lit bundle, which can't load in the DOM-less test runtime).
//
// Ported from d3-vtt's web/lib/api-response.js.

/**
 * Read a successful response's JSON body. A 204 No Content — what Huma returns
 * for a bodyless success (here, revoking an invitation) — has no body to parse,
 * so it resolves to null instead of surfacing a phantom "non-JSON response"
 * error. Anything else must parse as JSON.
 * @param {Response} response
 * @returns {Promise<any>}
 */
export async function readJsonBody(response) {
    if (response.status === 204) return null;
    try {
        return await response.json();
    } catch {
        throw new Error(`API error: ${response.status} ${response.statusText} (non-JSON response)`);
    }
}

/**
 * Build an Error from a failed response. Huma returns RFC7807
 * (`{title, status, detail, errors}`); d3-auth returns `{message}`. The status
 * is attached because callers branch on it — the invite view has to tell 404
 * (bad token) from 409 (already accepted, or the terms changed while reading).
 *
 * `details` carries Huma's per-field errors array, which is how a stale-version
 * 409 names the current version of each document.
 * @param {Response} response
 * @returns {Promise<Error & {status: number, details: any[]}>}
 */
export async function readErrorBody(response) {
    const body = await response.json().catch(() => ({}));
    const message =
        body.detail ||
        body.message ||
        body.title ||
        `API error: ${response.status} ${response.statusText}`;

    const error = /** @type {Error & {status: number, details: any[]}} */ (new Error(message));
    error.status = response.status;
    error.details = Array.isArray(body.errors) ? body.errors : [];
    return error;
}
